import React, { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useOutletContext } from "react-router-dom";

import PageLoader from "../../../../components/loaders/PageLoader";
import exportToCsv from "../../../../utils/exportToCsv";
import ExportUnitExclusionsFilters from "../../components/ExportUnitExclusions/ExportUnitExclusionsFilters";
import ExportUnitExclusionsHeader from "../../components/ExportUnitExclusions/ExportUnitExclusionsHeader";
import ExportUnitExclusionsTable from "../../components/ExportUnitExclusions/ExportUnitExclusionsTable";
import { useExportUnitExclusions } from "../../hooks/useExportUnitExclusions";
import { useCrumb } from "../../../../layouts/TrailProvider";
import {
  filterExportUnitExclusions,
  formatDateForDisplay,
  generateExportUnitExclusionFileName,
  uniqueExportUnitExclusionOperators,
  uniqueExportUnitExclusionUnits,
} from "../../utils/exportUnitExclusions.helpers";

import styles from "./ExportUnitExclusions.styles";

export default function ExportUnitExclusions() {

  useCrumb('Excluded Units', '/home/export-unit-exclusions');

  const { isSidebarExpanded } = useOutletContext();

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [filters, setFilters] = useState({ unit: [], operator: [] });

  const { rows, loading } = useExportUnitExclusions();

  const unitOptions = useMemo(
    () => uniqueExportUnitExclusionUnits(rows),
    [rows]
  );

  const operatorOptions = useMemo(
    () => uniqueExportUnitExclusionOperators(rows),
    [rows]
  );

  const filteredRows = useMemo(
    () => filterExportUnitExclusions(rows, searchQuery, filters, dateFilter),
    [rows, searchQuery, filters, dateFilter]
  );

  const handleExport = useCallback(() => {
    const rowsToExport = filteredRows.map((row) => ({
      unit: row.unit,
      operator: row.operator,
      startDate: formatDateForDisplay(row.startDate),
      endDate: formatDateForDisplay(row.endDate),
    }));

    const headerMap = {
      unit: "Unit",
      operator: "Operator",
      startDate: "Start Date",
      endDate: "End Date",
    };

    exportToCsv(generateExportUnitExclusionFileName(), rowsToExport, headerMap);
  }, [filteredRows]);

  return (
    <Box
      sx={{
        ...styles.fileContent,
        marginLeft: isSidebarExpanded ? "240px" : "95px",
        width: isSidebarExpanded ? "calc(100vw - 240px)" : "calc(100vw - 95px)",
        transition: "all 0.3s ease",
      }}
    >
      {loading ? (
        <PageLoader />
      ) : (
        <>
          <ExportUnitExclusionsHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={() => {}}
          />

          <ExportUnitExclusionsFilters
            filters={filters}
            setFilters={setFilters}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            unitOptions={unitOptions}
            operatorOptions={operatorOptions}
            onExport={handleExport}
          />

          <ExportUnitExclusionsTable rows={filteredRows} />
        </>
      )}
    </Box>
  );
}
